import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, BellRing, Play } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasPerm } from "../../lib/permissions";
import { PageHead, Field, Empty, ago } from "./common";

const BLANK = { alert_type: "rate_increase", threshold_pct: 10, route_id: "", port_id: "", keyword: "", channels: ["dashboard"], email_to: "", is_active: true };

export default function SmartAlerts() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [ports, setPorts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const canCreate = hasPerm(user, "sea_freight", "create");
  const canEdit = hasPerm(user, "sea_freight", "create");

  const load = useCallback(() => {
    Promise.all([
      api.get("/sea-freight/alerts"),
      api.get("/sea-freight/alert-logs", { params: { limit: 50 } }),
      api.get("/sea-freight/saved-routes"),
      api.get("/sea-freight/ports", { params: { limit: 300 } }),
    ]).then(([a, l, r, p]) => { setData(a.data); setLogs(l.data); setRoutes(r.data); setPorts(p.data.ports || []); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.post("/sea-freight/alerts", form);
      toast.success("Alert rule created");
      setOpen(false); setForm(BLANK); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggle = async (row) => {
    try { await api.put(`/sea-freight/alerts/${row.id}`, { is_active: !row.is_active }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/sea-freight/alerts/${id}`); toast.success("Alert rule deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const evaluate = async () => {
    try {
      const { data: r } = await api.post("/sea-freight/alerts/evaluate");
      toast.success(`${r.rules} rule(s) checked · ${r.fired} alert(s) fired`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const needsThreshold = form.alert_type.startsWith("rate_");
  const needsPort = form.alert_type.startsWith("port_");
  const needsKeyword = ["important_news", "shipping_disruption"].includes(form.alert_type);

  return (
    <div data-testid="smart-alerts-page">
      <PageHead title="Smart Alerts"
        subtitle="Rules are checked on every scheduled sync. Each event fires once — repeat notifications for the same event are suppressed."
        right={<>
          {canEdit && <Button data-testid="evaluate-alerts-btn" variant="outline" className="rounded-sm gap-1.5" onClick={evaluate}><Play className="w-4 h-4" /> Check now</Button>}
          {canCreate && <Button data-testid="add-alert-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={() => setOpen(true)}><Plus className="w-4 h-4" /> New alert</Button>}
        </>} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white border border-slate-200 rounded-sm">
          <header className="px-4 py-3 border-b border-slate-200"><h2 className="text-base font-semibold text-slate-900 font-head">My alert rules</h2></header>
          {data && data.alerts.length === 0 ? <div className="p-4"><Empty testid="alerts-rules-empty">No alert rules yet.</Empty></div> : (
            <div className="divide-y divide-slate-100">
              {(data?.alerts || []).map((a) => (
                <div key={a.id} data-testid={`alert-rule-${a.id}`} className="px-4 py-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{a.label}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {a.threshold_pct ? `Threshold ${a.threshold_pct}% · ` : ""}
                      {a.route_id ? `${routes.find((r) => r.id === a.route_id)?.route_name || "route"} · ` : ""}
                      {a.port_id ? `${ports.find((p) => p.id === a.port_id)?.port_name || "port"} · ` : ""}
                      {a.keyword ? `"${a.keyword}" · ` : ""}
                      {(a.channels || []).join(", ")}{a.email_to ? ` → ${a.email_to}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch data-testid={`toggle-alert-${a.id}`} checked={!!a.is_active} onCheckedChange={() => toggle(a)} />
                    <Button data-testid={`delete-alert-${a.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => remove(a.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-sm">
          <header className="px-4 py-3 border-b border-slate-200"><h2 className="text-base font-semibold text-slate-900 font-head">Alert history</h2></header>
          {logs.length === 0 ? <div className="p-4"><Empty testid="alert-logs-empty">No alerts fired yet.</Empty></div> : (
            <div className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
              {logs.map((l) => (
                <div key={l.id} data-testid={`alert-log-${l.id}`} className="px-4 py-3">
                  <div className="flex items-start gap-2">
                    <BellRing className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm text-slate-800">{l.title}</div>
                      <div className="text-xs text-slate-500">{l.body}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {ago(l.at)} · {(l.channels || []).join(", ")}
                        {l.email_sent && <Badge variant="outline" className="ml-1 rounded-full text-[10px] px-1.5 bg-emerald-50 text-emerald-700 border-emerald-200">email sent</Badge>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="alert-dialog">
          <DialogHeader><DialogTitle className="font-head">New alert rule</DialogTitle>
            <DialogDescription>Alerts appear on the dashboard and can also be emailed.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Field label="Alert type">
              <Select value={form.alert_type} onValueChange={(v) => setForm({ ...form, alert_type: v })}>
                <SelectTrigger data-testid="alert-type" className="bg-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(data?.types || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field></div>
            {needsThreshold && (
              <>
                <Field label="Threshold %"><Input data-testid="alert-threshold" type="number" min="0" value={form.threshold_pct} onChange={(e) => setForm({ ...form, threshold_pct: e.target.value })} className="bg-white h-9" /></Field>
                <Field label="Route (optional)">
                  <Select value={form.route_id || "all"} onValueChange={(v) => setForm({ ...form, route_id: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="alert-route" className="bg-white h-9"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All saved routes</SelectItem>{routes.map((r) => <SelectItem key={r.id} value={r.id}>{r.route_name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </>
            )}
            {needsPort && (
              <div className="sm:col-span-2"><Field label="Port (optional)">
                <Select value={form.port_id || "all"} onValueChange={(v) => setForm({ ...form, port_id: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="alert-port" className="bg-white h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72"><SelectItem value="all">Any port</SelectItem>{ports.map((p) => <SelectItem key={p.id} value={p.id}>{p.port_name} ({p.unlocode})</SelectItem>)}</SelectContent>
                </Select>
              </Field></div>
            )}
            {needsKeyword && (
              <div className="sm:col-span-2"><Field label="Keyword (optional)"><Input data-testid="alert-keyword" value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} placeholder="e.g. Red Sea, Mundra, surcharge" className="bg-white h-9" /></Field></div>
            )}
            <div className="sm:col-span-2"><Field label="Notification channels">
              <div className="flex flex-wrap gap-4 text-sm text-slate-700 pt-1">
                {["dashboard", "email"].map((ch) => (
                  <label key={ch} className="flex items-center gap-2">
                    <input data-testid={`alert-channel-${ch}`} type="checkbox" checked={form.channels.includes(ch)}
                      onChange={(e) => setForm({ ...form, channels: e.target.checked ? [...form.channels, ch] : form.channels.filter((c) => c !== ch) })} />
                    {ch === "dashboard" ? "ERP dashboard & in-app" : "Email"}
                  </label>
                ))}
              </div>
            </Field></div>
            {form.channels.includes("email") && (
              <div className="sm:col-span-2"><Field label="Email to"><Input data-testid="alert-email" value={form.email_to} onChange={(e) => setForm({ ...form, email_to: e.target.value })} placeholder={user?.email} className="bg-white h-9" /></Field></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-alert-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={save}>Create alert</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
